// useQueryParams.ts
import { useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

type SetOptions = { replace?: boolean };

export default function useQueryParams() {
    const navigate = useNavigate();
    const location = useLocation();

    // Fresh URLSearchParams anytime the search string changes
    const params = useMemo(
        () => new URLSearchParams(location.search),
        [location.search]
    );

    const commit = useCallback(
        (nextParams: URLSearchParams, { replace = true }: SetOptions = {}) => {
            const nextSearch = nextParams.toString(); // no leading "?"
            const curSearch = location.search.startsWith("?")
                ? location.search.slice(1)
                : location.search;

            // ✅ No-op guard: do nothing if the search is the same
            if (nextSearch === curSearch) return;

            navigate(
                {
                    pathname: location.pathname,
                    search: nextSearch ? `?${nextSearch}` : "",
                    hash: location.hash,
                },
                { replace, state: location.state }
            );
        },
        [navigate, location.pathname, location.search, location.hash, location.state]
    );

    const set = useCallback(
        (key: string, value: string | number | null | undefined, opts?: SetOptions) => {
            // Start from current params
            const next = new URLSearchParams(location.search);

            const curVal = next.get(key);
            const newVal =
                value === null || value === undefined || value === "" ? null : String(value);

            // ✅ No-op guard: if value wouldn't change, skip commit
            if ((newVal === null && curVal === null) || (newVal !== null && curVal === newVal)) {
                return;
            }

            if (newVal === null) next.delete(key);
            else next.set(key, newVal);

            commit(next, opts);
        },
        [location.search, commit]
    );

    const remove = useCallback(
        (key: string, opts?: SetOptions) => {
            const next = new URLSearchParams(location.search);

            // ✅ No-op guard: if key not present, skip
            if (!next.has(key)) return;

            next.delete(key);
            commit(next, opts);
        },
        [location.search, commit]
    );

    const removeMany = useCallback(
        (keys: string[], opts?: SetOptions) => {
            const next = new URLSearchParams(location.search);

            // Track whether anything actually changed
            let changed = false;
            for (const k of keys) {
                if (next.has(k)) {
                    next.delete(k);
                    changed = true;
                }
            }
            if (!changed) return; // ✅ No-op guard

            commit(next, opts);
        },
        [location.search, commit]
    );

    return { params, set, remove, removeMany };
}
